import type {
  ActivityOverride,
  ActivityQualification,
  BoardActivity,
  BoardDay,
  DemandDriver,
  DemandFormula,
} from './boardCoverage.js';
import { DEMAND_METRICS } from './boardCoverage.js';
import type { ActivityPresence, BoardAbsence } from './activityBoard.js';

// Row shapes as PostgREST returns them (snake_case), exported like
// PresenceRow — the SPAs read these tables directly under RLS (and write the
// desk-editable ones), and must map identically everywhere. No store class:
// S4-core has no service-role consumer; the S4-alerts job will add one.

/** DB driver values ↔ domain (identical since metrics v2, 0038 — kept as the single mapping point). */
const DRIVER_FROM_DB: Record<string, DemandDriver> = {
  always: 'always',
  departures: 'departures',
  arrivals: 'arrivals',
  stayovers: 'stayovers',
  breakfasts: 'breakfasts',
  covers: 'covers',
};
const DRIVER_TO_DB: Record<DemandDriver, string> = {
  always: 'always',
  departures: 'departures',
  arrivals: 'arrivals',
  stayovers: 'stayovers',
  breakfasts: 'breakfasts',
  covers: 'covers',
};

export function driverFromDb(value: string): DemandDriver {
  return DRIVER_FROM_DB[value] ?? 'always';
}

export function driverToDb(driver: DemandDriver): string {
  return DRIVER_TO_DB[driver];
}

export interface BoardActivityRow {
  id: number;
  label: string;
  window_start: string;
  window_end: string;
  window_note: string | null;
  demand_driver: string;
  demand_formula: Record<string, unknown> | null;
  min_demand: number | null;
  active: boolean;
  sort: number;
  team: string | null;
  /** Optional so a client built before the untimed migration still maps (absent → false). */
  untimed?: boolean;
  /** ADR-0015. Optional: absent on a client built before the registry migration. */
  load_metric_key?: string | null;
  badge_metric_key?: string | null;
}

/**
 * Keep only well-formed metric→number pairs. The SQL check constrains this
 * too, but the value arrives as untyped jsonb: anything unexpected is dropped
 * rather than allowed to poison the coverage sum with NaN (which would read as
 * "no demand" and silently hide a gap).
 */
function formulaFromDb(raw: Record<string, unknown> | null): DemandFormula | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const formula: DemandFormula = {};
  for (const metric of DEMAND_METRICS) {
    const value = raw[metric];
    if (typeof value === 'number' && Number.isFinite(value)) formula[metric] = value;
  }
  return Object.keys(formula).length === 0 ? null : formula;
}

export function rowToBoardActivity(row: BoardActivityRow): BoardActivity {
  return {
    id: row.id,
    label: row.label,
    windowStart: row.window_start,
    windowEnd: row.window_end,
    windowNote: row.window_note,
    demandDriver: driverFromDb(row.demand_driver),
    demandFormula: formulaFromDb(row.demand_formula ?? null),
    // Tolerate the column being absent (a client built before the migration
    // reached the deployed DB) — null means the default threshold of 1.
    minDemand: row.min_demand ?? null,
    active: row.active,
    sort: row.sort,
    team: row.team === 'menage' || row.team === 'receptionSalle' || row.team === 'cuisine' ? row.team : null,
    untimed: row.untimed ?? false,
    // Null when the registry migration has not reached this database, or when
    // the operator has not pointed the activity at a metric — the coverage path
    // then falls back to demandFormula (ADR-0015: keep the legacy column).
    loadMetricKey: row.load_metric_key ?? null,
    badgeMetricKey: row.badge_metric_key ?? null,
  };
}

export interface QualificationRow {
  activity_id: number;
  employee_id: string;
  is_main?: boolean;
}

export function rowToQualification(row: QualificationRow): ActivityQualification {
  return { activityId: row.activity_id, employeeId: row.employee_id, isMain: row.is_main === true };
}

export interface BoardDayRow {
  date: string;
  departures: number;
  arrivals: number;
  stayovers: number;
  occupancy: number | null;
  breakfasts: number;
  half_boards: number;
  /** Optional so a client built before the v3 migration still maps (falls back to covers − half_boards). */
  diners?: number;
  /** 0146 — optional for the same reason: a client built before the migration
   *  still maps, and reads them as zero, which is what they were. */
  diners_hotel?: number;
  diners_external_booked?: number;
  packed_lunches?: number;
  covers: number;
  note: string | null;
  updated_at: string;
  /** 0129 (M3) — optional for the same reason `diners` is: a client built
   *  before the migration still maps. Null and absent both mean "no feed has
   *  written this day", which is NOT zero guests. */
  guests_adults?: number | null;
  guests_children?: number | null;
  arrivals_guests?: number | null;
  departures_guests?: number | null;
}

export function rowToBoardDay(row: BoardDayRow): BoardDay {
  return {
    date: row.date,
    departures: row.departures,
    arrivals: row.arrivals,
    stayovers: row.stayovers,
    occupancy: row.occupancy ?? null,
    breakfasts: row.breakfasts,
    halfBoards: row.half_boards,
    packedLunches: row.packed_lunches ?? 0,
    dinersHotel: row.diners_hotel ?? 0,
    diners: row.diners ?? Math.max(row.covers - row.half_boards, 0),
    dinersExternalBooked: row.diners_external_booked ?? 0,
    covers: row.covers,
    note: row.note,
    updatedAt: row.updated_at,
    // `?? null` rather than `?? 0`: absent and zero are different answers, and
    // only M4 makes them present (0129).
    guestsAdults: row.guests_adults ?? null,
    guestsChildren: row.guests_children ?? null,
    arrivalsGuests: row.arrivals_guests ?? null,
    departuresGuests: row.departures_guests ?? null,
  };
}

export interface OverrideRow {
  id: number;
  activity_id: number;
  date: string;
  window_start: string | null;
  window_end: string | null;
  required: boolean;
  note: string | null;
  updated_at: string;
}

export function rowToOverride(row: OverrideRow): ActivityOverride {
  return {
    id: row.id,
    activityId: row.activity_id,
    date: row.date,
    windowStart: row.window_start,
    windowEnd: row.window_end,
    required: row.required,
    note: row.note,
    updatedAt: row.updated_at,
  };
}

// ── Presence + absence rows (consolidated at ladder step 6) ──────────────────
// `ActivityPresenceRow` and its mapper were duplicated verbatim in DayView,
// EmployeeWeek and MonthView. One definition, one mapper — the SupabaseBoardStore
// adapter is now the only caller that needs them, but they stay in this pure
// module (not in adapters/) because they are pure and the SPAs still read these
// tables directly while F2 is being retired.

export interface ActivityPresenceRow {
  activity_id: number;
  employee_id: string;
  date: string;
  status: 'declared' | 'assigned';
  source: ActivityPresence['source'];
  declared_by: string | null;
  message_id: number | null;
}

export function rowToPresence(r: ActivityPresenceRow): ActivityPresence {
  return {
    activityId: r.activity_id,
    employeeId: r.employee_id,
    date: r.date,
    status: r.status,
    source: r.source,
    declaredBy: r.declared_by,
    messageId: r.message_id,
  };
}

export interface BoardAbsenceRow {
  employee_id: string;
  date: string;
  source: BoardAbsence['source'];
  declared_by: string | null;
  message_id?: number | null;
}

export function rowToAbsence(r: BoardAbsenceRow): BoardAbsence {
  return {
    employeeId: r.employee_id,
    date: r.date,
    source: r.source,
    declaredBy: r.declared_by,
    messageId: r.message_id ?? null,
  };
}
